import { Company } from '@apis/Customers/model';
import { getRestrictedTagsGetRestrictedTags, postRestrictedTagsSaveRestrictedTag } from '@apis/TagManager';
import { RestrictedTag } from '@apis/TagManager/model';
import { useAsync } from '@react-hookz/web';
import { CompanyTenantPrereqService } from '@root/Components/Router/CompanyContent';
import { useEffect } from 'react';
import { inject, injectable, singleton } from 'tsyringe';
import { RequestThrottlingService } from '../AsyncBundler';
import { BaseCacheByTenant } from '../Customers/BaseCacheByTenant';
import { ICompanyContextToken } from '../Customers/CompanyContext';
import { useDiMemo } from '../DI';
import { ISupplementalFieldInfo } from '../QueryExpr';

class RestrictedTagCollection {
    private readonly byName = new Map<string, RestrictedTag>();
    private readonly byId = new Map<number, RestrictedTag>();

    public readonly restrictedTags: RestrictedTag[] = [];

    public get isEmpty() {
        return !this.restrictedTags.length;
    }

    public constructor(restrictedTags: RestrictedTag[]) {
        this.update(restrictedTags);
    }

    public getTag(nameOrId: string | number) {
        return typeof nameOrId === 'string' ? this.byName.get(nameOrId) : this.byId.get(nameOrId);
    }

    public getTags() {
        return [...this.byName.keys()];
    }

    public getValidTags(tags: (string | RestrictedTag)[]) {
        return tags.map((t) => (typeof t === 'string' ? t : t.TagName ?? '')).filter((t) => this.byName.get(t));
    }

    public sort() {
        return this.restrictedTags
            .map((t) => ({ name: (t.TagName ?? '').replace(/^\s+|\s+$/g, '').toLocaleLowerCase(), item: t }))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((t) => t.item);
    }

    public update(restrictedTags: RestrictedTag[]) {
        for (const tag of restrictedTags) {
            const existingTag = this.byId.get(tag.Id ?? 0);
            if (!existingTag && tag.IsActive) {
                this.restrictedTags.push(tag);
            }
            if (!tag.IsActive) {
                this.byName.delete(tag.TagName ?? '');
                this.byId.delete(tag.Id ?? 0);
            } else {
                this.byName.set(tag.TagName ?? '', tag);
                this.byId.set(tag.Id ?? 0, tag);
            }
        }
        const updatedList = this.restrictedTags.filter((t) => this.byId.has(t.Id ?? 0)).map((t) => this.byId.get(t.Id ?? 0)!);
        this.restrictedTags.splice(0, Infinity, ...updatedList);

        return this;
    }

    public getSchemaFieldInfo(tag: string, otherInfo?: ISupplementalFieldInfo) {
        const self = this;
        return {
            ...otherInfo,
            Flags: ['tag'],
            get HelpText() {
                return self.getTag(tag)?.Description;
            },
            get Icon() {
                return self.getTag(tag)?.Icon;
            },
        } as ISupplementalFieldInfo;
    }
}

@singleton()
class RestrictedTagCache extends BaseCacheByTenant<RestrictedTagCollection> {
    public constructor(@inject(CompanyTenantPrereqService) tenantPrereqSvc: CompanyTenantPrereqService) {
        super(tenantPrereqSvc);
    }
}

export interface IRestrictedTagCollection extends RestrictedTagCollection {}
@injectable()
export class RestrictedTagService {
    private restrictTagsThrottle = RequestThrottlingService.create(300);
    public constructor(
        @inject(ICompanyContextToken) private readonly company: Company,
        @inject(RestrictedTagCache) private readonly cache: RestrictedTagCache
    ) {}

    public async getRestrictedTags(): Promise<IRestrictedTagCollection> {
        return await this.cache.get(this.company.Id!, () =>
            getRestrictedTagsGetRestrictedTags()
                .then((r) => new RestrictedTagCollection(r))
                .catch(() => new RestrictedTagCollection([]))
        );
    }

    public async reload(): Promise<IRestrictedTagCollection> {
        const tags = await this.loadTags();
        return (
            this.cache.mutate(this.company.Id!, (c) => c.update(tags)) ??
            this.cache.get(this.company.Id!, async () => new RestrictedTagCollection(tags))
        );
    }

    public async save(restrictedTags: RestrictedTag[]) {
        const saved = await postRestrictedTagsSaveRestrictedTag(restrictedTags);
        this.cache.mutate(this.company.Id!, (c) => {
            c.update(saved);
        });

        return saved;
    }

    private async loadTags() {
        return this.restrictTagsThrottle.throttle(this.company.Id?.toString() ?? '', async () => {
            return await getRestrictedTagsGetRestrictedTags();
        });
    }
}

export function useRestrictedTags() {
    const restrictedTagSvc = useDiMemo(RestrictedTagService);
    const [{ result, status }, { execute }, { promise }] = useAsync(() => restrictedTagSvc.getRestrictedTags());
    useEffect(() => {
        execute();
    }, []);
    return { noTagRestrictions: !!result?.isEmpty, restrictedTags: result, loading: status === 'loading', promise, request: execute };
}
